import { useState } from 'react';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import { useCurrentOrganization } from '../../hooks/useCurrentOrganization';
import { useOrganizationStats } from '../../hooks/useOrganizationStats';
import apiClient from '../../api/client';

export function SubscriptionManageScreen() {
  const { pushScreen } = useTabNavigationStore();

  const { data: organization, error: orgError, refetch: refetchOrg } = useCurrentOrganization();
  const { data: organizationStats, error: statsError, refetch: refetchStats, isLoading: isLoadingStats } = useOrganizationStats();

  const [autoRenewalUpdating, setAutoRenewalUpdating] = useState(false);

  const handleAutoRenewalToggle = async () => {
    if (!organization || autoRenewalUpdating) return;
    setAutoRenewalUpdating(true);
    try {
      await apiClient.put(`/organizations/${organization.id}/auto-renewal`, {
        auto_renewal: !organization.auto_renewal,
      });
      refetchOrg();
    } catch (e) {
      console.error('Failed to toggle auto-renewal', e);
    } finally {
      setAutoRenewalUpdating(false);
    }
  };

  const subscriptionTier = (organization?.subscription_tier || 'team') as string;

  const limits = {
    members: organizationStats?.limits.max_members ?? -1,
    assistants: organizationStats?.limits.max_assistants ?? -1,
    athletes: organizationStats?.limits.max_athletes ?? -1,
    groups: organizationStats?.limits.max_groups ?? -1,
    coaches: organizationStats?.limits.max_coaches ?? -1,
  };

  const usage = {
    members: organizationStats?.members ?? 0,
    assistants: organizationStats?.assistants ?? 0,
    athletes: organizationStats?.athletes ?? 0,
    groups: organizationStats?.groups ?? 0,
    coaches: organizationStats?.coaches ?? 0,
  };

  const tierNames: Record<string, string> = {
    free: 'FREE', standard: 'STANDARD', pro: 'PRO', academy: 'ACADEMY',
    basic: 'BASIC', premium: 'PREMIUM', team: 'TEAM', club: 'CLUB', enterprise: 'ENTERPRISE',
  };

  const currentTierName = tierNames[subscriptionTier] || subscriptionTier.toUpperCase();

  const formatLimit = (value: number | null) => {
    if (value === null || value === -1) return '\u221E';
    return value;
  };

  const renderUsageBar = (current: number, max: number) => {
    if (max === -1) return null;
    const percentage = Math.min((current / max) * 100, 100);
    const isNearLimit = percentage >= 80;

    return (
      <div className="w-full h-1.5 rounded-full mt-1" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${percentage}%`,
            backgroundColor: isNearLimit ? 'var(--danger)' : 'var(--accent)',
          }}
        />
      </div>
    );
  };

  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader
        title="Моя подписка"
        subtitle="Управление тарифным планом организации"
      />

      {/* Current tier */}
      <Card>
        <div className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{currentTierName}</div>
        <p className="text-sm mb-2" style={{ color: 'var(--text-hint)' }}>{organization?.name || 'Организация'}</p>

        {/* Subscription status and expiry */}
        {organization?.subscription_status && (
          <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--separator)' }}>
            <div className="flex justify-between items-center text-sm">
              <span style={{ color: 'var(--text-hint)' }}>Статус:</span>
              <span style={{ color: 'var(--text-primary)' }}>
                {organization.subscription_status === 'trial' && 'Триал'}
                {organization.subscription_status === 'active' && 'Активна'}
                {organization.subscription_status === 'expired' && 'Истекла'}
              </span>
            </div>

            {organization.subscription_expires_at && (
              <div className="flex justify-between items-center text-sm mt-2">
                <span style={{ color: 'var(--text-hint)' }}>
                  {organization.subscription_status === 'trial' ? 'Триал до:' : 'Действует до:'}
                </span>
                <span style={{ color: 'var(--text-primary)' }}>
                  {new Date(organization.subscription_expires_at).toLocaleDateString('ru-RU', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Auto-renewal toggle (only for paid tiers) */}
      {subscriptionTier !== 'free' && (
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-[15px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                Автопродление
              </h3>
              <p className="text-[13px] mt-0.5" style={{ color: 'var(--text-hint)' }}>
                Автоматическое продление подписки
              </p>
            </div>
            <button
              onClick={handleAutoRenewalToggle}
              disabled={autoRenewalUpdating}
              className="relative flex-shrink-0 w-12 h-7 rounded-full transition-colors"
              style={{
                backgroundColor: organization?.auto_renewal ? 'var(--accent)' : 'var(--bg-secondary)',
                opacity: autoRenewalUpdating ? 0.5 : 1,
              }}
            >
              <span
                className="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform"
                style={{
                  transform: organization?.auto_renewal ? 'translateX(20px)' : 'translateX(0)',
                }}
              />
            </button>
          </div>
        </Card>
      )}

      {/* Limits usage */}
      {(orgError || statsError) ? (
        <Card>
          <div className="py-4 text-center">
            <p className="text-[15px] mb-2" style={{ color: 'var(--text-hint)' }}>
              Не удалось загрузить данные
            </p>
            <button
              onClick={() => { refetchOrg(); refetchStats(); }}
              className="text-[15px] font-medium"
              style={{ color: 'var(--accent)' }}
            >
              Повторить
            </button>
          </div>
        </Card>
      ) : isLoadingStats ? (
        <Card>
          <div className="py-4 text-center">
            <p className="text-[15px]" style={{ color: 'var(--text-hint)' }}>Загрузка...</p>
          </div>
        </Card>
      ) : (
      <Card>
        <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Остатки лимитов</h3>
        <div className="space-y-3">
          {[
            { label: 'Участники', key: 'members' as const },
            { label: 'Направления', key: 'assistants' as const },
            { label: 'Спортсмены', key: 'athletes' as const },
            { label: 'Группы', key: 'groups' as const },
            { label: 'Тренеры', key: 'coaches' as const },
          ].map((item) => (
            <div key={item.key}>
              <div className="flex justify-between items-center">
                <span style={{ color: 'var(--text-hint)' }}>{item.label}</span>
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {usage[item.key]} / {formatLimit(limits[item.key])}
                </span>
              </div>
              {renderUsageBar(usage[item.key], limits[item.key])}
            </div>
          ))}
        </div>
      </Card>
      )}

      {/* Actions */}
      <div className="space-y-2">
        {/* Renew button — for paid tiers only */}
        {subscriptionTier !== 'free' && (
          <Button
            onClick={() =>
              pushScreen('tier-change-confirm', {
                newTierId: subscriptionTier,
                autoRenewal: true,
                isRenewal: true,
              })
            }
            fullWidth
          >
            Продлить подписку
          </Button>
        )}
        <Button
          onClick={() => pushScreen('subscription-plans')}
          fullWidth
          variant={subscriptionTier !== 'free' ? 'secondary' : 'primary'}
        >
          Изменить тариф
        </Button>
        <Button onClick={() => pushScreen('subscription-help')} variant="secondary" fullWidth>
          Справка о тарифах
        </Button>
      </div>
    </div>
  );
}
